import { create } from "zustand";
import { getStatuses } from "../api/statuses";
import { deleteChecklist, updateChecklist, getChecklists } from "../api/checklists";

export const useSettingsState = create((set, get) => ({
  pipelines: {},
  selectedPipeline: 0,
  statuses: [],
  selectedStatus: null,
  fields: [],
  hiddenFields: [],
  checklists: [],
  searchTerm: '',            
  favorites: [],             
  history: [],               
  
  
  copyModalVisible: false,
  copyingChecklist: null,
  selectedTargetPipeline: null,
  selectedTargetStatus: null,
  copyInProgress: false,
  
  setPipelines: (pipelines) => set({ pipelines }),
  setSelectedPipeline: (pipeline) => set({ 
    selectedPipeline: pipeline,
    selectedStatus: null,
    hiddenFields: [],
    checklists: []
  }),
  setStatuses: (statuses) => set({ statuses }),
  setSelectedStatus: (status) => set((state) => ({ 
    selectedStatus: status,
    hiddenFields: status?.hidden_fields || [],
    checklists: status?.checklists || []
  })),
  setFields: (fields) => set({ fields }),
  setHiddenFields: (hiddenFields) => set({ hiddenFields }),
  setChecklists: (incomingChecklists) => {
    const unique = [];
    const seen= new Set();
    incomingChecklists.forEach(c => {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        unique.push(c);
      }
    });
    set({ checklists: unique });
  },
  
  
  openCopyModal: (checklist) => set({ 
    copyModalVisible: true, 
    copyingChecklist: checklist,
    selectedTargetPipeline: null,
    selectedTargetStatus: null 
  }),
  
  closeCopyModal: () => set({ 
    copyModalVisible: false, 
    copyingChecklist: null,
    selectedTargetPipeline: null,
    selectedTargetStatus: null,
    copyInProgress: false
  }),
  
  setSelectedTargetPipeline: (pipeline) => set({ 
    selectedTargetPipeline: pipeline,
    selectedTargetStatus: null 
  }),
  
  setSelectedTargetStatus: (status) => set({ selectedTargetStatus: status }),
  
  setCopyInProgress: (inProgress) => set({ copyInProgress: inProgress }),
  
  handleDeleteChecklist: async (checklistId) => {
    const { checklists, selectedStatus } = get();
    const lastChecklists = checklists;
    
    set({ checklists: checklists.filter(checklist => checklist.id !== checklistId) });
    
    try {
      const success = await deleteChecklist(selectedStatus.amo_status_id, checklistId);
      if (!success) {
        throw new Error('Не удалось удалить чеклист');
      }
    } catch (error) {
      set({ checklists: lastChecklists });
      throw error;
    }
  },
  
  handleMoveChecklist: async (checklistId, direction) => {
    const { checklists, selectedStatus } = get();
    const lastChecklists = [...checklists];
    const checklist = checklists.find(cl => cl.id === checklistId);
    const newPosition = direction === 'up' ? checklist.position - 1 : checklist.position + 1;
    const secondChecklist = checklists.find(cl => cl.position === newPosition);
    
    if (secondChecklist) {
      set({
        checklists: checklists.map((cl) =>
          cl.id === secondChecklist.id ? { ...secondChecklist, position: checklist.position } : cl
        )
      });
    }
    
    const updatedChecklist = { ...checklist, position: newPosition };
    
    set({
      checklists: checklists.map((cl) =>
        cl.id === updatedChecklist.id ? updatedChecklist : cl
      )
    });
    
    try {
      const res = await updateChecklist(selectedStatus.amo_status_id, updatedChecklist.id, updatedChecklist);
      if (res.error) {
        throw new Error('Не удалось переместить чеклист');
      }
      const updatedChecklists = await getChecklists(selectedStatus.amo_status_id);
      set({ checklists: updatedChecklists });
    } catch (error) {
      set({ checklists: lastChecklists });
      throw error;
    }
  },
  
  getFilteredStatuses: () => {
    const { statuses, selectedPipeline } = get();
    
    if (!selectedPipeline || typeof selectedPipeline !== 'object' || !selectedPipeline.id) {
      return statuses;
    }
    return statuses.filter((status) => selectedPipeline.id === status.pipeline_id);
  },
  
  updateData: async (updateStatuses = false) => {
    try {
      const statuses = await getStatuses(updateStatuses);
      
      if (!statuses || statuses.length === 0) {
        throw new Error('Не удалось получить статусы');
      }
      
      statuses.forEach((status) => {
        if (!Object.keys(get().pipelines).includes(status.pipeline_id)) {
          set((state) => ({
            pipelines: {
              ...state.pipelines,
              [status.pipeline_id]: { 
                name: status.pipeline_name, 
                id: status.pipeline_id 
              }
            }
          }));
        }
      });
      
      set({ 
        statuses,
        fields: Object.values(AMOCRM.constant("account").cf)
      });
      
      return statuses;
    } catch (error) {
      throw error;
    }
  },

  setSearchTerm: (searchTerm) => set({ searchTerm }),
  toggleFavoriteStatus: (statusId) => set((state) => {
    const exists = state.favorites.includes(statusId);
    const favorites = exists
      ? state.favorites.filter(id => id !== statusId)
      : [...state.favorites, statusId];
    try { localStorage.setItem('favorites', JSON.stringify(favorites)); } catch {};
    return { favorites };
  }),
  recordStatusHistory: (statusId) => set((state) => {
    const history = [statusId, ...state.history.filter(id => id !== statusId)];
    const limited = history.slice(0, 10);
    try { localStorage.setItem('history', JSON.stringify(limited)); } catch {};
    return { history: limited };
  }),
  loadFavoritesFromStorage: () => {
    try {
      const stored = JSON.parse(localStorage.getItem('favorites') || '[]');
      set({ favorites: Array.isArray(stored) ? stored : [] });
    } catch { }
  },
  loadHistoryFromStorage: () => set((state) => {
    try {
      const stored = localStorage.getItem('history');
      const history = stored ? JSON.parse(stored) : [];
      return { history };
    } catch { return {}; }
  }),
  clearHistory: () => set(() => {
    try { localStorage.removeItem('history'); } catch {};
    return { history: [] };
  }),
})); 